import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AccountService from '@/services/AccountService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            setIsLoading(false);
            return;
        }
        AccountService.getMe()
            .then(user => setCurrentUser(user))
            .catch(() => localStorage.removeItem('token'))
            .finally(() => setIsLoading(false));
    }, []);

    const finishLogin = useCallback(async (token) => {
        localStorage.setItem('token', token);
        const user = await AccountService.getMe();
        setCurrentUser(user);
        return { success: true };
    }, []);

    const login = useCallback(async (email, password) => {
        const result = await AccountService.login(email, password);
        if (result?.requiresTwoFactor) {
            return {
                success: false,
                requiresTwoFactor: true,
                pendingToken: result.pendingToken,
                twoFactorMethod: result.twoFactorMethod ?? 'Totp',
            };
        }
        if (result?.token) return finishLogin(result.token);
        return { success: false, error: 'Invalid email or password' };
    }, [finishLogin]);

    const completeTwoFactorLogin = useCallback(async (pendingToken, code) => {
        const result = await AccountService.verifyTwoFactorLogin(pendingToken, code);
        if (result?.token) return finishLogin(result.token);
        return { success: false };
    }, [finishLogin]);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        setCurrentUser(null);
        navigate('/login');
    }, [navigate]);

    return (
        <AuthContext.Provider value={{
            currentUser,
            isLoading,
            isAuthenticated: !!currentUser,
            login,
            completeTwoFactorLogin,
            logout,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
